/**
 * SubscriptionDropdown - Azure subscription selector
 *
 * Fetches and displays user's Azure subscriptions.
 * Used as the first picker in the cascading resource selection.
 */

import { useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';
import { useSubscriptions } from '@/hooks/useAzureResources';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  refreshButton: {
    flexShrink: 0,
  },
});

export interface SubscriptionDropdownProps {
  /** Currently selected subscription ID */
  value: string;
  /** Callback when selection changes */
  onChange: (subscriptionId: string) => void;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Validation error message */
  error?: string;
}

export const SubscriptionDropdown = ({
  value,
  onChange,
  label = 'Subscription',
  required = false,
  disabled = false,
  error,
}: SubscriptionDropdownProps) => {
  const styles = useStyles();

  const {
    data: subscriptions,
    isLoading,
    error: fetchError,
    refetch,
  } = useSubscriptions();

  // Build options from subscriptions. The full GUID is shown as secondary text
  // and remains searchable so users can paste/type a full ID to find a match.
  const options = useMemo<SearchableComboboxOption[]>(() => {
    return (subscriptions ?? []).map((sub) => ({
      value: sub.subscriptionId,
      text: sub.displayName,
      secondaryText: sub.subscriptionId,
      searchTerms: [sub.subscriptionId, sub.displayName],
    }));
  }, [subscriptions]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const validationState = error || fetchError ? 'error' : 'none';
  const validationMessage = error ?? fetchError?.message ?? null;

  return (
    <div className={styles.container}>
      <Field
        label={label}
        required={required}
        validationState={validationState}
        validationMessage={validationMessage}
      >
        <div className={styles.fieldRow}>
          <SearchableCombobox
            className={styles.combobox}
            options={options}
            value={value}
            onChange={onChange}
            disabled={disabled || isLoading}
            placeholder={isLoading ? 'Loading subscriptions...' : 'Select a subscription'}
            aria-label={label}
          />
          <Button
            className={styles.refreshButton}
            appearance="subtle"
            icon={isLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
            onClick={handleRefresh}
            disabled={isLoading || disabled}
            aria-label="Refresh subscriptions"
            title="Refresh subscriptions"
          />
        </div>
      </Field>
    </div>
  );
};

SubscriptionDropdown.displayName = 'SubscriptionDropdown';
